import { useEffect } from "react";
import { useLocation } from "@/lib/router";
import { API_BASE } from "./api";

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;
type FbqFunction = ((...args: unknown[]) => void) & {
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
  callMethod?: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: FbqFunction;
    _fbq?: unknown;
  }
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "";
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || "";
const DEBUG = import.meta.env.VITE_ANALYTICS_DEBUG === "true";

let initialized = false;
let lastClientEvent: { key: string; at: number } | null = null;

export function initAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  if (GA_ID) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer?.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, { send_page_view: false });
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`);
  }

  if (META_PIXEL_ID) {
    window.fbq = function fbq(...args: unknown[]) {
      if (window.fbq?.callMethod) window.fbq.callMethod(...args);
      else window.fbq?.queue?.push(args);
    } as FbqFunction;
    window._fbq = window.fbq;
    window.fbq.queue = [];
    window.fbq.loaded = true;
    window.fbq.version = "2.0";
    window.fbq("init", META_PIXEL_ID);
    injectScript("https://connect.facebook.net/en_US/fbevents.js");
  }
}

export function trackPageView(path: string, title?: string) {
  const payload = compactParams({ page_path: path, page_title: title || document.title });
  if (shouldDropDuplicate("page_view", payload)) return;
  if (DEBUG) console.info("[analytics] page_view", payload);
  window.gtag?.("event", "page_view", payload);
  window.fbq?.("track", "PageView");
  sendFirstPartyEvent("page_view", { ...payload, path });
}

export function trackEvent(eventName: string, params: AnalyticsParams = {}) {
  const payload = compactParams({
    ...params,
    path: typeof window === "undefined" ? undefined : `${window.location.pathname}${window.location.search}`,
    referrer: typeof document === "undefined" ? undefined : document.referrer,
  });
  if (shouldDropDuplicate(eventName, payload)) return;
  if (DEBUG) console.info("[analytics] event", eventName, payload);
  window.gtag?.("event", eventName, payload);
  window.fbq?.("trackCustom", eventName, payload);
  sendFirstPartyEvent(eventName, payload);
}

export function trackLandingView(params: { productId: string; productKind: string; variant: string; priority?: string }) {
  trackEvent("landing_view", {
    product_id: params.productId,
    product_kind: params.productKind,
    variant: params.variant,
    priority: params.priority,
  });
}

export function trackCtaClick(params: {
  productId: string;
  productKind: string;
  variant?: string;
  cta: string;
  destination: string;
  location: string;
}) {
  trackEvent("cta_click", {
    product_id: params.productId,
    product_kind: params.productKind,
    variant: params.variant,
    cta: params.cta,
    destination: params.destination,
    location: params.location,
  });
}

export function AnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
}

function injectScript(src: string) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function sendFirstPartyEvent(name: string, params: AnalyticsParams) {
  if (typeof window === "undefined") return;
  const body = JSON.stringify({
    name,
    ...params,
    productId: params.product_id || params.productId,
    productKind: params.product_kind || params.productKind,
    campaign: campaignParams(),
  });
  const url = `${API_BASE}/analytics/events`;
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    return;
  }
  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

function campaignParams() {
  const params = new URLSearchParams(window.location.search);
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"];
  return Object.fromEntries(keys.map((key) => [key, params.get(key) || ""]).filter(([, value]) => value));
}

function compactParams(params: AnalyticsParams) {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));
}

function shouldDropDuplicate(eventName: string, params: AnalyticsParams) {
  const now = Date.now();
  const key = `${eventName}:${JSON.stringify(params)}`;
  if (lastClientEvent?.key === key && now - lastClientEvent.at < 750) return true;
  lastClientEvent = { key, at: now };
  return false;
}
